/**
 * The Admin's Painéis de TV (TV-PANEL-LINKS, D647, ADR-0123) - framework-neutral view-models.
 *
 *  - **Operações › Painéis de TV** (`tv_panels`): the wall screens of the operations room. An operator REGISTERS a panel (a name, the views it
 *    shows, its scope - the whole platform, one country or one city - and whether the views rotate), receives the D561 PAIRING
 *    CODE once (`XXXX-XXXX`, single use) and types it on the TV. The panel's connection comes from its device's heartbeat; the TV can be paired
 *    again with a new code ("Gerar novo código" while it waits, "Parear de novo" once paired) or the panel revoked.
 *
 * The console only presents: the server authorizes every call at the panel's scope and re-validates every field (the same rules as below).
 * The scope a form offers is what the operator's grants reach (the same set as the top scope selector), never a wider one. Views, scopes,
 * states, connections, revoke reasons and event types are codes shown through the vocabulary catalog (`tv.*`). Neutral: no country, city or
 * language is named here.
 */
import type { OperatingMarketView, TvConnection, TvPanelInput, TvPanelView, TvRevokeReason, TvScopeView, TvViewCode } from '@vello/api-client';
import { TV_VIEW_CODES } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { sameRegion } from './scope.js';
import type { OperatorScope } from './shell.js';

/** What the operator may do with TV panels (the server re-authorizes every call at the panel's own scope). */
export function tvPanelAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'device:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'device:manage', target: 'GEO' }),
  };
}

/** The views a panel can show, in the order the form offers them (the design's: Mapa, KPIs, Fila, Cidades, Serviços). */
export const TV_VIEW_CHOICES: readonly TvViewCode[] = TV_VIEW_CODES;
/** The reasons a revocation can name, in the order the form offers them. */
export const TV_REVOKE_REASON_CHOICES: readonly TvRevokeReason[] = ['lost_or_stolen', 'replaced', 'decommissioned', 'suspected_compromise', 'other'];
/** The rotation interval a new panel starts with (used when the server accepts it). */
export const TV_DEFAULT_ROTATE_SECONDS = 45;
export const TV_NAME_MAX = 80;

// ---- the form ----------------------------------------------------------------------------------------------------------------------------

export interface TvPanelDraft {
  readonly name: string;
  /** In the order they were clicked: that is the order they show (and rotate) in. */
  readonly views: readonly TvViewCode[];
  readonly rotate: boolean;
  readonly rotateSeconds: number;
  /** `tvScopeValue` of the chosen option. */
  readonly scope: string;
}

/** A scope as a `<select>` value: `P` (platform), `C:BR` (a country), `R:BR-MG` (a state), `M:br:slug` (a market). */
export function tvScopeValue(scope: TvScopeView): string {
  switch (scope.kind) {
    case 'PLATFORM':
      return 'P';
    case 'COUNTRY':
      return `C:${scope.country.toUpperCase()}`;
    case 'REGION':
      return `R:${scope.region.toUpperCase()}`;
    case 'MARKET':
      return `M:${scope.market}`;
  }
}

export function tvScopeFromValue(value: string): TvScopeView | undefined {
  if (value === 'P') return { kind: 'PLATFORM' };
  if (value.startsWith('C:') && value.length > 2) return { kind: 'COUNTRY', country: value.slice(2) };
  if (value.startsWith('R:') && value.length > 2) return { kind: 'REGION', region: value.slice(2) };
  if (value.startsWith('M:') && value.length > 2) return { kind: 'MARKET', market: value.slice(2) };
  return undefined;
}

/** The scope the operator's top selector is on, as a panel scope (a city, a state, a country or the whole platform). */
export function tvScopeOfOperator(scope: OperatorScope): TvScopeView {
  if (scope.city !== undefined) return { kind: 'MARKET', market: scope.city };
  if (scope.region !== undefined) return { kind: 'REGION', region: scope.region.toUpperCase() };
  if (scope.country !== undefined) return { kind: 'COUNTRY', country: scope.country.toUpperCase() };
  return { kind: 'PLATFORM' };
}

/**
 * The scopes a panel can be given by this operator: what the grants reach (the top selector's `allowed` set), in its order - the platform,
 * then each country followed by its states and its cities. `current` (a panel being edited) is kept in the list even when it is outside the set, so the
 * form never silently shows another scope than the panel has.
 */
export function tvScopeChoices(allowed: readonly OperatorScope[], current?: TvScopeView): readonly TvScopeView[] {
  const out: TvScopeView[] = [];
  const seen = new Set<string>();
  const push = (s: TvScopeView): void => {
    const k = tvScopeValue(s);
    if (!seen.has(k)) {
      seen.add(k);
      out.push(s);
    }
  };
  for (const s of allowed) push(tvScopeOfOperator(s));
  if (current !== undefined) push(current);
  return out;
}

/** The starting scope of a new panel: the top selector's scope when the operator may use it, else the first choice. */
export function tvDefaultScope(scope: OperatorScope, choices: readonly TvScopeView[]): string {
  const wanted = tvScopeValue(tvScopeOfOperator(scope));
  const first = choices[0];
  return choices.some((c) => tvScopeValue(c) === wanted) ? wanted : first !== undefined ? tvScopeValue(first) : 'P';
}

/** The `Cidades` view compares markets, so it needs a scope of more than one. */
export const tvViewAllowedInScope = (view: TvViewCode, scope: TvScopeView | undefined): boolean => !(view === 'CITIES' && scope?.kind === 'MARKET');

/** Rotation needs two or more views to alternate between. */
export const tvMayRotate = (views: readonly TvViewCode[]): boolean => views.length >= 2;

/** Click a view chip: it joins at the end of the order, or leaves. */
export function toggleTvView(views: readonly TvViewCode[], view: TvViewCode): readonly TvViewCode[] {
  return views.includes(view) ? views.filter((v) => v !== view) : [...views, view];
}

/** The draft after the operator changed the scope: a city scope cannot show Cidades, so that view leaves and rotation is re-checked. */
export function withTvScope(draft: TvPanelDraft, scope: string): TvPanelDraft {
  const parsed = tvScopeFromValue(scope);
  const views = draft.views.filter((v) => tvViewAllowedInScope(v, parsed));
  return { ...draft, scope, views, rotate: draft.rotate && tvMayRotate(views) };
}

/** The draft after a view chip was clicked: rotation switches itself off when fewer than two views remain. */
export function withTvViewToggled(draft: TvPanelDraft, view: TvViewCode): TvPanelDraft {
  const views = toggleTvView(draft.views, view);
  return { ...draft, views, rotate: draft.rotate && tvMayRotate(views) };
}

export function newTvDraft(scope: string, rotateSecondsOptions: readonly number[]): TvPanelDraft {
  const rotateSeconds = rotateSecondsOptions.includes(TV_DEFAULT_ROTATE_SECONDS) ? TV_DEFAULT_ROTATE_SECONDS : (rotateSecondsOptions[0] ?? TV_DEFAULT_ROTATE_SECONDS);
  return { name: '', views: [], rotate: false, rotateSeconds, scope };
}

export function tvDraftOf(panel: TvPanelView): TvPanelDraft {
  return { name: panel.name, views: panel.views, rotate: panel.rotate, rotateSeconds: panel.rotateSeconds, scope: tvScopeValue(panel.scope) };
}

/** The draft is complete: a name of 1-80 characters and at least one view (the server re-validates everything). */
export function tvDraftValid(draft: TvPanelDraft): boolean {
  const name = draft.name.trim();
  const scope = tvScopeFromValue(draft.scope);
  return name.length > 0 && name.length <= TV_NAME_MAX && draft.views.length > 0 && scope !== undefined && draft.views.every((v) => tvViewAllowedInScope(v, scope));
}

/** The body of the call. Rotation is sent off for a single view; the name is trimmed. */
export function tvInputOf(draft: TvPanelDraft, homeMarket?: string): TvPanelInput | undefined {
  const scope = tvScopeFromValue(draft.scope);
  if (scope === undefined || !tvDraftValid(draft)) return undefined;
  return { name: draft.name.trim(), views: draft.views, rotate: draft.rotate && tvMayRotate(draft.views), rotateSeconds: draft.rotateSeconds, scope, ...(homeMarket !== undefined ? { homeMarket } : {}) };
}

/**
 * Where a NEW panel's device is registered: the city the operator's top selector is on, when that city lies inside the chosen scope; otherwise
 * nothing is sent and the server picks the scope's own market (or its first). The server re-checks that the market is inside the scope and reachable.
 */
export function tvHomeMarket(top: OperatorScope, scope: TvScopeView | undefined, markets: readonly OperatingMarketView[]): string | undefined {
  const city = top.city;
  if (city === undefined || scope === undefined) return undefined;
  const market = markets.find((m) => m.id === city);
  const country = (market?.countryCode ?? city.slice(0, 2)).toUpperCase();
  switch (scope.kind) {
    case 'PLATFORM':
      return city;
    case 'COUNTRY':
      return country === scope.country.toUpperCase() ? city : undefined;
    case 'REGION':
      return market?.regionCode !== undefined && sameRegion(market.regionCode, scope.region) ? city : undefined;
    case 'MARKET':
      return scope.market === city ? city : undefined;
  }
}

/** Whether the draft differs from the saved panel (the drawer's save stays off until it does). */
export function tvDraftChanged(panel: TvPanelView, draft: TvPanelDraft): boolean {
  const saved = tvDraftOf(panel);
  return saved.name !== draft.name.trim() || saved.rotate !== draft.rotate || saved.rotateSeconds !== draft.rotateSeconds || saved.scope !== draft.scope || saved.views.join() !== draft.views.join();
}

// ---- the list ----------------------------------------------------------------------------------------------------------------------------

/** What the status column says: revoked wins over the heartbeat; otherwise the connection. */
export type TvPanelStatus = 'REVOKED' | TvConnection;
export const tvPanelStatus = (panel: TvPanelView): TvPanelStatus => (panel.state === 'REVOKED' ? 'REVOKED' : panel.connection);

/** Panels in use first (connected, no signal, never connected), the revoked last; newest first inside each group. */
const STATUS_ORDER: Readonly<Record<TvPanelStatus, number>> = { CONNECTED: 0, NO_SIGNAL: 1, NEVER: 2, REVOKED: 3 };
export function sortTvPanels(panels: readonly TvPanelView[]): readonly TvPanelView[] {
  return [...panels].sort((a, b) => STATUS_ORDER[tvPanelStatus(a)] - STATUS_ORDER[tvPanelStatus(b)] || b.createdAt.localeCompare(a.createdAt) || a.panelId.localeCompare(b.panelId));
}

export type TvTone = 'neutral' | 'success' | 'warning' | 'danger';
export const tvStatusTone = (status: TvPanelStatus): TvTone => (status === 'CONNECTED' ? 'success' : status === 'NO_SIGNAL' ? 'warning' : status === 'REVOKED' ? 'danger' : 'neutral');

/** A panel the operator can still change: not revoked, and the grants hold the manage word. */
export const mayChangeTvPanel = (panel: TvPanelView, mayManage: boolean): boolean => mayManage && panel.state === 'ACTIVE';

/**
 * "Excluir" (a terminal soft delete: the panel leaves the lists, its history stays for the audit): offered to a manager for an ACTIVE panel (it is revoked first, so the TV stops at
 * once) and for a REVOKED one. A deleted panel is never listed, so there is no third state.
 */
export const mayDeleteTvPanel = (panel: TvPanelView, mayManage: boolean): boolean => mayManage && (panel.state === 'ACTIVE' || panel.state === 'REVOKED');

/** What the delete confirmation says: an active panel's TV stops now; a revoked one only leaves the list. */
export const tvDeleteBodyKey = (panel: TvPanelView): 'tvpanels.delete.bodyActive' | 'tvpanels.delete.bodyRevoked' =>
  panel.state === 'ACTIVE' ? 'tvpanels.delete.bodyActive' : 'tvpanels.delete.bodyRevoked';

/** The sign-in name of an operator, or the first eight characters of the account id when no name is on record. */
export const tvOperatorName = (operators: Readonly<Record<string, string>>, id: string): string => operators[id] ?? id.slice(0, 8);

/** Events newest first (the server already sends them so; a screen that merges a refresh keeps the order). */
export const sortTvEvents = <T extends { readonly occurredAt: string }>(events: readonly T[]): readonly T[] => [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));

/** The TV's device has not typed its code yet: a code can be re-issued as it is. A paired one needs "Parear de novo" (the TV on the wall stops). */
export const tvDevicePaired = (panel: TvPanelView): boolean => panel.device.state === 'paired';
